import { apiRequest } from '../lib/api-client';

/** Internal chat (context.md sections 57-59). Colleagues, not customers — that is the inbox. */

export type ConversationType = 'CHANNEL' | 'DIRECT';

export interface ChatConversation {
  id: string;
  type: ConversationType;
  name: string | null;
  topic: string | null;
  is_private: boolean;
  member: boolean;
  unread: number;
  /** Kept at the top of this person's list. Personal: nobody else's sidebar changes. */
  pinned: boolean;
  /** Who a direct conversation is with; null for a channel. */
  partner_name: string | null;
  last_message_at: string | null;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  author_id: string | null;
  body: string;
  mentions: string[];
  reply_to_id: string | null;
  edited_at: string | null;
  created_at: string;
  reactions?: { emoji: string; users: string[] }[];
}

/**
 * Searching the history of every room the person belongs to, plus the open channels.
 *
 * The server decides what that means; nothing here narrows or widens it. A search that reached
 * further than the rooms somebody is in would be a private conversation leaking through a
 * search box.
 */
export function searchChat(query: string, limit = 20): Promise<{ messages: ChatMessage[] }> {
  return apiRequest(`/v1/chat/search?q=${encodeURIComponent(query)}&limit=${limit}`);
}

export function listChatConversations(): Promise<{ conversations: ChatConversation[] }> {
  return apiRequest('/v1/chat/conversations');
}

export function listChatMessages(
  conversationId: string,
): Promise<{ messages: ChatMessage[]; next_cursor: string | null }> {
  return apiRequest(`/v1/chat/conversations/${conversationId}/messages?limit=100`);
}

export function sendChatMessage(conversationId: string, body: string): Promise<ChatMessage> {
  return apiRequest(`/v1/chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: { body },
  });
}

export function createChannel(input: {
  name: string;
  isPrivate?: boolean;
}): Promise<ChatConversation> {
  return apiRequest('/v1/chat/channels', { method: 'POST', body: input });
}

export function joinConversation(id: string): Promise<void> {
  return apiRequest(`/v1/chat/conversations/${id}/join`, { method: 'POST' });
}

/**
 * Opens the direct conversation with somebody, creating it the first time.
 *
 * The endpoint has existed since the module was built and nothing in the interface called it, so
 * the "Diretas" section could only ever be empty: a team chat you cannot message a person in.
 */
export function openDirect(userId: string): Promise<{ id: string; type: ConversationType }> {
  return apiRequest('/v1/chat/direct', { method: 'POST', body: { userId } });
}

export function pinConversation(id: string, pinned: boolean): Promise<{ pinned: boolean }> {
  return apiRequest(`/v1/chat/conversations/${id}/pin`, { method: 'PUT', body: { pinned } });
}

export function markRead(id: string): Promise<void> {
  return apiRequest(`/v1/chat/conversations/${id}/read`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Who is in the room, and who runs it
// ---------------------------------------------------------------------------

export interface RoomMember {
  userId: string;
  name: string;
  role: 'MEMBER' | 'ADMIN';
  joinedAt: string;
}

export function listRoomMembers(conversationId: string): Promise<{ members: RoomMember[] }> {
  return apiRequest(`/v1/chat/conversations/${conversationId}/members`);
}

export function addRoomMembers(
  conversationId: string,
  userIds: string[],
): Promise<{ added: number }> {
  return apiRequest(`/v1/chat/conversations/${conversationId}/members`, {
    method: 'POST',
    body: { userIds },
  });
}

export function removeRoomMember(conversationId: string, userId: string): Promise<void> {
  return apiRequest(`/v1/chat/conversations/${conversationId}/members/${userId}`, {
    method: 'DELETE',
  });
}

export function setRoomMemberRole(
  conversationId: string,
  userId: string,
  role: 'MEMBER' | 'ADMIN',
): Promise<{ members: RoomMember[] }> {
  return apiRequest(`/v1/chat/conversations/${conversationId}/members/${userId}`, {
    method: 'PATCH',
    body: { role },
  });
}
